import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

let deferredPrompt = null;

// Capture the install prompt globally
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event('pwaPromptReady'));
  });
}

export function usePwaInstall() {
  const [canInstall, setCanInstall] = useState(!!deferredPrompt);

  useEffect(() => {
    const handler = () => setCanInstall(true);
    window.addEventListener('pwaPromptReady', handler);
    window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferredPrompt = e; setCanInstall(true); });
    return () => window.removeEventListener('pwaPromptReady', handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanInstall(false);
    return result.outcome === 'accepted';
  };

  return { canInstall, install };
}

export function PwaInstallBanner() {
  const { canInstall, install } = usePwaInstall();
  const [dismissed, setDismissed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    setIsStandalone(window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);
    const d = sessionStorage.getItem('pwa_banner_dismissed');
    if (d) setDismissed(true);
  }, []);

  // Show after user interaction + delay
  useEffect(() => {
    if (dismissed || !isMobile || isStandalone) return;
    let timer;
    const showBanner = () => {
      timer = setTimeout(() => setVisible(true), 4000);
      window.removeEventListener('scroll', showBanner);
      window.removeEventListener('touchstart', showBanner);
      window.removeEventListener('click', showBanner);
    };
    window.addEventListener('scroll', showBanner, { once: true });
    window.addEventListener('touchstart', showBanner, { once: true });
    window.addEventListener('click', showBanner, { once: true });
    return () => { clearTimeout(timer); window.removeEventListener('scroll', showBanner); window.removeEventListener('touchstart', showBanner); window.removeEventListener('click', showBanner); };
  }, [dismissed, isMobile, isStandalone]);

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('pwa_banner_dismissed', '1');
  };

  // Show on mobile, not already installed, not dismissed
  if (isStandalone || dismissed || !isMobile || !visible) return null;

  // For iOS (no beforeinstallprompt), show manual instructions
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);

  if (!canInstall && !isIos) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-3 animate-fade-in" data-testid="pwa-install-banner">
      <div className="bg-zinc-900 border border-[#C5A059]/30 p-4 flex items-center gap-3 shadow-2xl shadow-black/60 mx-auto max-w-md">
        <div className="w-10 h-10 bg-[#C5A059]/15 flex items-center justify-center shrink-0">
          <Download size={18} className="text-[#C5A059]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Aggiungi alla Home</p>
          <p className="text-[10px] text-zinc-400 mt-0.5">
            {isIos ? 'Tocca "Condividi" e poi "Aggiungi a schermata Home"' : 'Installa AeroDeals per un accesso rapido'}
          </p>
        </div>
        {!isIos && canInstall && (
          <button onClick={install} className="bg-[#C5A059] text-black px-3 py-2 text-xs font-medium hover:bg-[#D4B271] transition-colors shrink-0" data-testid="pwa-install-btn">
            Installa
          </button>
        )}
        <button onClick={dismiss} className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-white transition-colors shrink-0" data-testid="pwa-dismiss-btn">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
